import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { outlineBtnCls } from './styles';
import FilterSelect, { SelectOption } from './FilterSelect';

interface SelectFilterDef {
  type?: 'select';
  key: string;
  label: string;
  value: string;
  options: SelectOption[];
  onChange: (v: string) => void;
  title?: string;
}

interface CustomFilterDef {
  type: 'custom';
  key: string;
  label: string;
  /** Arbitrary control(s) for filters that aren't a single select (e.g. the answer filter). */
  node: ReactNode;
}

export type FilterDef = SelectFilterDef | CustomFilterDef;

interface Props {
  filters: FilterDef[];
  /** Number of these filters currently applied — drives the count badge. */
  activeCount: number;
  /** Button label (defaults to "Add filter"). */
  label?: string;
}

/**
 * "Add filter" popover holding the secondary filters as labeled fields. This is
 * the Option A overflow + Option B affordance in one: a single button (with an
 * active-count badge) that reveals the long-tail filters, while applied values
 * surface as dismissible chips via <ActiveFilters>.
 */
export default function AddFilterMenu({ filters, activeCount, label = 'Add filter' }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        aria-expanded={open}
        aria-haspopup="dialog"
        className={outlineBtnCls(activeCount > 0)}
      >
        <SlidersHorizontal className="h-4 w-4" />
        {label}
        {activeCount > 0 && (
          <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-primary-600 px-1.5 text-xs font-medium text-white">
            {activeCount}
          </span>
        )}
      </button>
      {open && (
        <div
          role="dialog"
          aria-label={label}
          className="absolute right-0 z-20 mt-2 w-72 max-w-[calc(100vw-2rem)] space-y-3 rounded-lg border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-800"
        >
          {filters.map((f) => (
            <div key={f.key}>
              <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">{f.label}</label>
              {f.type === 'custom' ? (
                f.node
              ) : (
                <FilterSelect value={f.value} onChange={f.onChange} options={f.options} label={f.label} title={f.title} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
